import { type FC, type KeyboardEvent as ReactKeyboardEvent, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { ModalBody } from 'dibs-elements/exports/ModalBody.tsx';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton.tsx';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer.tsx';
import { Radio } from 'dibs-elements/exports/Radio.tsx';
import { ModalSpinner } from 'dibs-elements/exports/ModalSpinner.tsx';

import dibsCss from 'dibs-css';
import classNames from 'classnames';

import styles from './VerticalToggleModal.scss';

const radioGroupName = 'vertical-toggle-modal';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    createListingBaseUrl: string;
};

const createUrl = (baseUrl: string, vertical: string | null): string => {
    if (!vertical) {
        return baseUrl;
    }
    try {
        const [path, search] = baseUrl.split('?');
        const params = new URLSearchParams(search || '');
        params.set('vertical', vertical);
        return `${path}?${params.toString()}`;
    } catch {
        return `${baseUrl}?vertical=${vertical}`;
    }
};

export const VerticalToggleModal: FC<Props> = ({ isOpen, onClose, createListingBaseUrl }) => {
    const intl = useIntl();
    const [isLoading, setLoading] = useState(false);
    const [vertical, setVertical] = useState<string | null>(null);

    const handleKeyDown = useCallback(
        (value: string) => (e: ReactKeyboardEvent<HTMLElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setVertical(value);
            }
        },
        []
    );

    return (
        <ModalContainer
            isOpen={isOpen}
            onClose={onClose}
            preventBackScroll={false}
            closeOnEsc
            closeOnOutsideClick
            modalPosition="center"
            modalSize="default"
        >
            <ModalCloseButton onClick={onClose} />
            <h1
                className={classNames(
                    dibsCss.sassyFontHeaderMedium,
                    dibsCss.textCenter,
                    dibsCss.ptSmall,
                    dibsCss.mbSmall
                )}
            >
                <FormattedMessage
                    id="dal.VerticalToggleModal.title"
                    defaultMessage="Create a Listing"
                />
            </h1>
            <p
                className={classNames(
                    dibsCss.sassyFontBodyLarge,
                    dibsCss.textCenter,
                    dibsCss.mbSmall
                )}
            >
                <FormattedMessage
                    id="dal.VerticalToggleModal.subtitle"
                    defaultMessage="Select the type of item you'd like to list."
                />
            </p>
            <ModalBody topSpacing="small">
                <fieldset className={classNames(dibsCss.mb0, dibsCss.border0)}>
                    <div className={classNames(dibsCss.flex, dibsCss.flexCol, dibsCss.gapXsmall)}>
                        <div
                            role="button"
                            tabIndex={0}
                            className={classNames(
                                styles.radioOptionContainer,
                                dibsCss.border,
                                dibsCss.borderSolid,
                                dibsCss.borderDolphin,
                                dibsCss.pSmall,
                                dibsCss.cursorPointer
                            )}
                            onClick={() => setVertical('fashion')}
                            onKeyDown={handleKeyDown('fashion')}
                        >
                            <Radio
                                dataTn="vertical-toggle-fashion"
                                checked={vertical === 'fashion'}
                                label={
                                    <FormattedMessage
                                        id="dal.VerticalToggleModal.fashion.label"
                                        defaultMessage="Clothes / Accessories / Objects"
                                    />
                                }
                                onChange={() => setVertical('fashion')}
                                name={radioGroupName}
                                value="fashion"
                                labelContainerClass={styles.radio}
                            />
                        </div>
                        <div
                            role="button"
                            tabIndex={0}
                            className={classNames(
                                styles.radioOptionContainer,
                                dibsCss.border,
                                dibsCss.borderSolid,
                                dibsCss.borderDolphin,
                                dibsCss.pSmall,
                                dibsCss.cursorPointer
                            )}
                            onClick={() => setVertical('jewelry')}
                            onKeyDown={handleKeyDown('jewelry')}
                        >
                            <Radio
                                dataTn="vertical-toggle-jewelry"
                                checked={vertical === 'jewelry'}
                                label={
                                    <FormattedMessage
                                        id="dal.VerticalToggleModal.fashionJewelry.label"
                                        defaultMessage="Fashion Jewelry"
                                    />
                                }
                                onChange={() => setVertical('jewelry')}
                                name={radioGroupName}
                                value="jewelry"
                                labelContainerClass={styles.radio}
                            />
                        </div>
                    </div>
                </fieldset>
                <Button
                    dataTn="vertical-toggle-modal-continue"
                    type="primary"
                    size="large"
                    fullWidth
                    disabled={!vertical || isLoading}
                    onClick={() => {
                        if (vertical) {
                            setLoading(true);
                            window.location.href = handleLocaleUrl(
                                createUrl(createListingBaseUrl, vertical),
                                intl.locale
                            );
                        }
                    }}
                    className={dibsCss.mtLarge}
                >
                    <FormattedMessage
                        id="dal.VerticalToggleModal.continue"
                        defaultMessage="Continue"
                    />
                </Button>
            </ModalBody>
            <ModalSpinner isOpen={isLoading} dataTn="vertical-toggle-modal-spinner" />
        </ModalContainer>
    );
};
